import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

export type CentroUiPhase = 'idle' | 'ringing' | 'connected' | 'wrapup';

/** Payload from timeline4 phone log when the agent marks disposition in CRM instead of the Centro wrap-up panel. */
export interface CentroCrmDispositionRequest {
  disposition: string;
  remarks?: string;
}

/**
 * Root singleton for Centro call-leg UI phase.
 * Also the bridge for CRM-timeline auto-dispose: PhoneComponent cannot reach CallingComponent
 * directly, so timeline emits via requestCrmDisposition() and the widget subscribes to crmDisposition$.
 */
@Injectable({ providedIn: 'root' })
export class CentroUiStateService {
  private legEpoch = 0;
  private liveLegEpoch = 0;
  private phase: CentroUiPhase = 'idle';
  private lastPhone = '';
  // CRM timeline → Centro widget: fire-and-forget so wrap-up can complete with the same disposition.
  private crmDispositionSubject = new Subject<CentroCrmDispositionRequest>();
  readonly crmDisposition$: Observable<CentroCrmDispositionRequest> = this.crmDispositionSubject.asObservable();

  beginNewLeg(phone = ''): number {
    this.legEpoch += 1;
    this.liveLegEpoch = this.legEpoch;
    this.phase = 'ringing';
    this.lastPhone = String(phone || '').replace(/\D/g, '').slice(-10);
    console.log('[CentroUI] beginNewLeg', { epoch: this.liveLegEpoch, phone: this.lastPhone });
    return this.liveLegEpoch;
  }

  markConnected(epoch?: number): void {
    if (epoch && epoch !== this.liveLegEpoch) {
      console.log('[CentroUI] ignore stale connected', { epoch, live: this.liveLegEpoch });
      return;
    }
    this.phase = 'connected';
    console.log('[CentroUI] markConnected', { epoch: this.liveLegEpoch, phone: this.lastPhone });
  }

  markWrapUp(): void {
    this.phase = 'wrapup';
    console.log('[CentroUI] markWrapUp', { epoch: this.liveLegEpoch, phone: this.lastPhone });
  }

  resetAfterDispose(): void {
    this.liveLegEpoch = 0;
    this.phase = 'idle';
    this.lastPhone = '';
    console.log('[CentroUI] resetAfterDispose');
  }

  isLiveLeg(epoch?: number): boolean {
    return !epoch || epoch === this.liveLegEpoch;
  }

  shouldHandleSipEnd(epoch?: number): boolean {
    if (this.phase === 'wrapup') {
      return true;
    }
    if (epoch && epoch !== this.liveLegEpoch) {
      console.log('[CentroUI] ignore stale sip end', { epoch, live: this.liveLegEpoch });
      return false;
    }
    return this.phase === 'connected' || this.phase === 'ringing';
  }

  canAcceptIncomingUi(): boolean {
    return this.phase === 'idle' || this.phase === 'wrapup';
  }

  getPhase(): CentroUiPhase {
    return this.phase;
  }

  getLiveEpoch(): number {
    return this.liveLegEpoch;
  }

  getLastPhone(): string {
    return this.lastPhone;
  }

  /** True while Centro is waiting for wrap-up dispose (dialer panel or CRM timeline). */
  isWrapUpPending(): boolean {
    return this.phase === 'wrapup';
  }

  /**
   * Called from timeline4 PhoneComponent after CRM customer-response save succeeds.
   * Ignored unless wrap-up is pending — live/idle calls must not be disposed from CRM.
   * CallingComponent then runs the same submitCentroDisposition() path as the wrap-up panel.
   */
  requestCrmDisposition(disposition: string, remarks?: string): boolean {
    const reason = String(disposition || '').trim();
    if (!reason || this.phase !== 'wrapup') {
      return false;
    }
    console.log('[CentroUI] requestCrmDisposition', { reason, phase: this.phase });
    this.crmDispositionSubject.next({
      disposition: reason,
      remarks: String(remarks || '').trim(),
    });
    return true;
  }
}
